import { useQuery } from "@tanstack/react-query";
import {
  CircleDashed,
  CircleDollarSign,
  CircleStop,
  Coins,
  EyeOff,
  MessageSquareText,
  Timer,
} from "lucide-react";
import { parseAsArrayOf, parseAsString, useQueryState } from "nuqs";
import { api } from "../../api";
import { useCancelTask } from "../../hooks/use-actions";
import { compactTokens, elapsed, shortRepo, usd } from "../../lib/format";
import { actorName } from "../../lib/people";
import { isDetachedTask, runSpend, runStations } from "../../lib/tasks";
import { useFactory } from "../../store";
import type { AgentSummary, Artifact, FactoryLayout, RunListItem, Task, TaskSpend } from "../../types";
import { ArtifactList } from "../artifact-list";
import { EmptyState } from "../page";
import { TimeAgo } from "../time-ago";
import { Alert, AlertDescription, AlertTitle } from "../ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../ui/alert-dialog";
import { Button } from "../ui/button";
import { Card, CardAction, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { CurrentRunCard } from "./current-run-card";
import { RouteStepper } from "./route-stepper";
import { RunRow } from "./run-row";
import { StationFilter } from "./station-filter";

export type TaskDetail = Awaited<ReturnType<typeof api.task>>;

/** What the viewer may see and do on a work order. */
export interface WorkOrderAccess {
  runDetail: boolean;
  actions: boolean;
}

export function useTaskDetail(id: string | null | undefined) {
  return useQuery({
    queryKey: ["task", id],
    queryFn: () => api.task(id!),
    enabled: Boolean(id),
    refetchInterval: 8000,
  });
}

export function isOpenWorkOrder(task: Task): boolean {
  return !["completed", "failed", "cancelled"].includes(task.status);
}

/** Runs newest first, what they spent, and the stations the work order passes through. */
export function useProduction(data: TaskDetail) {
  const { agentById, layout } = useFactory();
  return productionOf(data, layout, agentById);
}

export function productionOf(
  data: TaskDetail,
  layout: FactoryLayout | undefined,
  agentById: Map<string, AgentSummary>,
) {
  const { task } = data;
  const runs = [...data.runs].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  const detached = isDetachedTask(task, layout);
  const intake = layout?.intake ?? [];
  // Work orders started on a lane by hand skip intake; show it only when it ran or still has to.
  const viaIntake = !task.lane || runs.some((r) => intake.includes(r.agentId));
  const route = detached
    ? [task.currentAgentId ?? ""]
    : [...(viaIntake ? intake : []), ...((task.lane && layout?.lanes[task.lane]?.stations) || [])];
  const pipeline = route
    .map((agentId) => agentById.get(agentId))
    .filter((a): a is AgentSummary => Boolean(a));
  return { runs, spend: runSpend(runs), detached, pipeline };
}

/** Repository, base branch and who created the work order, then what it has spent. */
export function WorkOrderMeta({ task, spend }: { task: Task; spend: TaskSpend }) {
  return (
    <div className="space-y-1">
      <p className="text-sm text-muted-foreground">
        <span className="font-mono">
          {shortRepo(task.repositoryUrl)}@{task.baseRef}
        </span>{" "}
        · created <TimeAgo at={task.createdAt} /> by {actorName(task.createdBy) ?? "the tracker"}
      </p>
      <SpendLine spend={spend} />
    </div>
  );
}

export function PullFromLineButton({ taskId, size }: { taskId: string; size?: "sm" }) {
  const cancelTask = useCancelTask(taskId);
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button variant="destructive" size={size}>
          <CircleStop /> Pull from line
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Pull this work order from the line?</AlertDialogTitle>
          <AlertDialogDescription>
            Any running station is stopped and the work order is cancelled. This cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Keep it running</AlertDialogCancel>
          <AlertDialogAction variant="destructive" onClick={() => cancelTask.mutate()}>
            Pull from line
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/** The route, the station working now, and every station run so far. */
export function WorkOrderProduction({ data, access }: { data: TaskDetail; access: WorkOrderAccess }) {
  const { agentById } = useFactory();
  const { task, currentRun } = data;
  const { runs, spend, detached, pipeline } = useProduction(data);
  return (
    <>
      {pipeline.length > 0 && (
        <RouteStepper
          task={task}
          runs={runs}
          pipeline={pipeline}
          lanePending={!detached && !task.lane}
          finish={detached ? "Issues filed" : "Shipped"}
        />
      )}
      {currentRun ? (
        <CurrentRunCard
          run={currentRun}
          task={task}
          agent={agentById.get(currentRun.agentId)}
          timeline={access.runDetail}
          actions={access.actions}
        />
      ) : (
        <Card>
          <CardContent>
            <EmptyState
              icon={CircleDashed}
              title="No station has picked this up yet"
              description="It will start as soon as the first station is free."
              className="border-0"
            />
          </CardContent>
        </Card>
      )}
      {runs.length > 0 && <ProductionHistory task={task} runs={runs} spend={spend} access={access} />}
    </>
  );
}

/** Stations unticked in the production history; empty shows every station, so the URL stays clean. */
const hiddenStationsParam = parseAsArrayOf(parseAsString).withDefault([]);

function ProductionHistory({
  task,
  runs,
  spend,
  access,
}: {
  task: Task;
  runs: RunListItem[];
  spend: TaskSpend;
  access: WorkOrderAccess;
}) {
  const { agentById } = useFactory();
  const [hide, setHide] = useQueryState("hideStations", hiddenStationsParam);
  const hidden = new Set(hide);
  const visibleRuns = runs.filter((r) => !hidden.has(r.agentId));
  return (
    <Card>
      <CardHeader>
        <CardTitle>Production history</CardTitle>
        <CardDescription>
          {visibleRuns.length < runs.length && `${visibleRuns.length} of `}
          {runs.length} station run{runs.length === 1 ? "" : "s"}, newest first
        </CardDescription>
        <CardAction className="flex items-start gap-4">
          <SpendTotal spend={spend} />
          <StationFilter
            stations={runStations(runs)}
            hidden={hidden}
            agentById={agentById}
            onChange={(next) => setHide(next.length ? next : null)}
          />
        </CardAction>
      </CardHeader>
      <CardContent className="space-y-2">
        {visibleRuns.length === 0 ? (
          <EmptyState
            icon={EyeOff}
            title="Every station is hidden"
            description="Tick a station in the filter to see its runs."
          >
            <Button variant="outline" size="sm" onClick={() => setHide(null)}>
              Show all stations
            </Button>
          </EmptyState>
        ) : (
          visibleRuns.map((run) => (
            <RunRow
              key={run.id}
              run={run}
              task={task}
              agent={agentById.get(run.agentId)}
              detail={access.runDetail}
              actions={access.actions}
            />
          ))
        )}
      </CardContent>
    </Card>
  );
}

export function ArtifactsCard({ artifacts }: { artifacts: Artifact[] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Artifacts</CardTitle>
        <CardDescription>Branches, pull requests and reports</CardDescription>
      </CardHeader>
      <CardContent>
        {artifacts.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nothing produced yet.</p>
        ) : (
          <ArtifactList artifacts={artifacts} />
        )}
      </CardContent>
    </Card>
  );
}

export function OperatorInstructions({ instructions }: { instructions: string }) {
  return (
    <Alert>
      <MessageSquareText />
      <AlertTitle>Operator instructions</AlertTitle>
      <AlertDescription>{instructions}</AlertDescription>
    </Alert>
  );
}

/** Agent time, tokens and cost for the whole work order. */
function SpendLine({ spend }: { spend: TaskSpend }) {
  const items = [
    spend.agentMs !== null && {
      id: "time",
      icon: Timer,
      text: `${elapsed(spend.agentMs)} agent time`,
      title: "Summed across station runs; parallel stations add up",
    },
    spend.totalTokens !== null && {
      id: "tokens",
      icon: Coins,
      text: compactTokens(spend.totalTokens),
      title: `${spend.totalTokens.toLocaleString()} tokens`,
    },
    spend.costUsd !== null && {
      id: "cost",
      icon: CircleDollarSign,
      text: `${usd(spend.costUsd)} spent`,
      title:
        spend.unbilled > 0
          ? `${spend.unbilled} run${spend.unbilled === 1 ? "" : "s"} not billed yet`
          : "Model cost across station runs",
    },
  ].filter((item) => item !== false);
  if (items.length === 0) return null;
  return (
    <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground tabular-nums">
      {items.map(({ id, icon: Icon, text, title }) => (
        <span key={id} title={title} className="inline-flex items-center gap-1.5">
          <Icon className="size-3.5" aria-hidden="true" />
          <span className="text-foreground">{text}</span>
        </span>
      ))}
    </p>
  );
}

/** What every station run on this work order has cost, summed from the same usage the rows show. */
function SpendTotal({ spend }: { spend: TaskSpend }) {
  if (spend.costUsd === null && spend.totalTokens === null) return null;
  return (
    <div className="text-right">
      <p className="text-base font-medium tabular-nums">
        {spend.costUsd === null ? "Not billed yet" : usd(spend.costUsd)}
      </p>
      <p className="text-xs text-muted-foreground tabular-nums">
        {spend.totalTokens !== null && (
          <span title={`${spend.totalTokens.toLocaleString()} tokens`}>
            {compactTokens(spend.totalTokens)}
          </span>
        )}
        {spend.costUsd !== null && spend.unbilled > 0 && (
          <span>
            {" "}
            · {spend.unbilled} run{spend.unbilled === 1 ? "" : "s"} not billed yet
          </span>
        )}
      </p>
    </div>
  );
}
